import { useEffect, useRef } from "react";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { pushGtmDonorSupportChannel } from "@/common/helpers/gtm";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { RouteType } from "@/common/hooks/amplitude/useLogRouteChangePageVisits";

export type SupportChannel = "forethought" | "assistant" | "pending";

export interface SupportChannelResolution<TReason extends string = string> {
  channel: SupportChannel;
  reason?: TReason;
  routeType: RouteType;
}

interface UsePublishSupportChannelProps {
  resolution: SupportChannelResolution;
  path: string;
  enabled: boolean;
  event: AmplitudeEvents.DonorSupportChannelResolved | AmplitudeEvents.OrganizationSupportChannelResolved;
}

export const usePublishSupportChannel = ({ resolution, path, enabled, event }: UsePublishSupportChannelProps) => {
  const { logAmplitudeEvent } = useAmplitude();
  const { channel, reason, routeType } = resolution;
  const lastResolutionKey = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      lastResolutionKey.current = null;
      return;
    }
    if (channel === "pending") {
      return;
    }

    const resolutionKey = `${channel}|${reason}|${path}`;
    if (lastResolutionKey.current === resolutionKey) {
      return;
    }
    lastResolutionKey.current = resolutionKey;

    pushGtmDonorSupportChannel({ donor_support_channel: channel });
    logAmplitudeEvent(event, { channel, reason, routeType, path });
  }, [channel, enabled, event, logAmplitudeEvent, path, reason, routeType]);
};
